import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Header from '../shared/TailwindComponents/Header';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import Table from '../shared/TailwindComponents/Table';
import Badge from '../shared/TailwindComponents/Badge';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import {
  PiTrashLight,
  PiFolderOpenLight,
  PiPlusLight,
  PiClockCounterClockwiseLight,
} from 'react-icons/pi';
import { showModal, hideModal } from '../../store/actions/modalActions';
import { showSidePanel, hideSidePanel } from '../../store/actions/sidePanelActions';
import { fetchProjects, deleteProject } from '../../store/actions/Projects/ProjectActions';
import CreateProjectForm from './CreateProjectForm';
import AuditLogPanel from '../AuditLog/AuditLogPanel';
import DeleteProjectConfirmation from './DeleteProjectConfirmation';
import { formatDateTime } from '../../utils/functions';
import debounce from 'lodash.debounce';
import CollaboratorBadge from '../shared/TailwindComponents/CollaboratorsBadge';
import clsx from 'clsx';
import { FeatureTour, projectsListOrientationSteps } from '../shared/FeatureTour';
import { useFeatureTour } from '../../hooks/useFeatureTour';

// ─── Config ─────────────────────────────────────────────────────

const statusColorMap: Record<string, BadgeColor> = {
  active: 'green',
  archived: 'gray',
  completed: 'blue',
};

const statusLabelMap: Record<string, string> = {
  active: 'Active',
  archived: 'Archived',
  completed: 'Completed',
};

// ─── Component ──────────────────────────────────────────────────

const ProjectsList: React.FC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();

  const { items, totalItems, isLoading } = useSelector((state: RootState) => state.projects);

  const queryParams = new URLSearchParams(location.search);
  const [isCreating, setIsCreating] = useState(() => queryParams.get('add') === 'true');
  const [showAuditPanel, setShowAuditPanel] = useState(false);
  const [auditProjectUuid, setAuditProjectUuid] = useState('');
  const [auditProjectTitle, setAuditProjectTitle] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] }>({});
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const {
    isRunning: isProjectsListTourRunning,
    handleJoyrideCallback: handleProjectsListTourCallback,
  } = useFeatureTour({
    tourId: 'projects-list-orientation',
    steps: projectsListOrientationSteps,
    readyToShow: !isLoading,
    autoStartDelay: 600,
  });

  // ─── Data Fetching ───────────────────────────────────────────

  const statusFilter = selectedFilters['status']?.[0] || '';

  const fetchPage = useCallback(
    (page: number) => {
      dispatch(fetchProjects({
        page, page_size: 20,
        sort_by: sortBy, sort_order: sortOrder,
        ...(searchTerm ? { search_term: searchTerm } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
      }));
    },
    [dispatch, sortBy, sortOrder, searchTerm, statusFilter]
  );

  useEffect(() => {
    if (!isCreating) fetchPage(1);
  }, [fetchPage, isCreating]);

  const debouncedSearch = useCallback(debounce((term: string) => { setSearchTerm(term); }, 300), []);

  // ─── Handlers ─────────────────────────────────────────────────

  const handleCreateClose = useCallback((newUuid?: string) => {
    setIsCreating(false);
    history.replace('/projects');
    if (newUuid) { history.push(`/projects/${newUuid}`); }
    else { fetchPage(1); }
  }, [history, fetchPage]);

  const handleDelete = (projectUuid: string, projectTitle: string) => {
    dispatch(showModal({
      showModal: true,
      type: 'custom',
      message: t('Delete Project'),
      subMsg: t('This action cannot be undone.'),
      icon: <PiTrashLight className="w-6 h-6 text-red-600" />,
      width: '500px',
      content: (
        <DeleteProjectConfirmation
          projectName={projectTitle}
          onConfirm={async () => {
            await dispatch(deleteProject(projectUuid));
            setSelectedIds((prev) => prev.filter((id) => id !== projectUuid));
            dispatch(hideModal());
          }}
          onCancel={() => dispatch(hideModal())}
        />
      ),
      onCancel: () => {},
    }));
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return;
    dispatch(showModal({
      showModal: true,
      type: 'custom',
      message: t(selectedIds.length > 1 ? 'Delete {{count}} projects?' : 'Delete this project?', { count: selectedIds.length }),
      subMsg: t('This action cannot be undone.'),
      icon: <PiTrashLight className="w-6 h-6 text-red-600" />,
      width: '500px',
      content: (
        <DeleteProjectConfirmation
          projectCount={selectedIds.length}
          onConfirm={async () => {
            for (const id of selectedIds) { await dispatch(deleteProject(id)); }
            setSelectedIds([]);
            dispatch(hideModal());
          }}
          onCancel={() => dispatch(hideModal())}
        />
      ),
      onCancel: () => {},
    }));
  };

  const handleSortChange = (sortKey: string, order: 'asc' | 'desc') => {
    const sortFieldMap: Record<string, string> = { 'Name': 'title', 'Status': 'status', 'Resources': 'resource_count', 'Created': 'created_at' };
    setSortBy(sortFieldMap[sortKey] || 'created_at');
    setSortOrder(order);
  };

  const handleFilterChange = (filterName: string, selectedValues: string[]) => {
    setSelectedFilters((prev) => ({ ...prev, [filterName]: selectedValues }));
  };

  const handleClearAllFilters = () => {
    setSelectedFilters({});
    setSearchTerm('');
  };

  // ─── Creation form ───────────────────────────────────────────

  if (isCreating) {
    return (
      <div className="flex flex-col overflow-hidden relative h-screen">
        <CreateProjectForm onClose={handleCreateClose} />
      </div>
    );
  }

  // ─── Filter config ────────────────────────────────────────────

  const filters = [
    {
      id: 'status',
      name: t('Status'),
      options: [
        { value: 'active', label: t('Active') },
        { value: 'archived', label: t('Archived') },
        { value: 'completed', label: t('Completed') },
      ],
    },
  ];

  const sortOptions = [
    { value: 'created_at_desc', label: t('Newest First'), current: sortBy === 'created_at' && sortOrder === 'desc' },
    { value: 'created_at_asc', label: t('Oldest First'), current: sortBy === 'created_at' && sortOrder === 'asc' },
    { value: 'title_asc', label: t('Name (A-Z)'), current: sortBy === 'title' && sortOrder === 'asc' },
    { value: 'title_desc', label: t('Name (Z-A)'), current: sortBy === 'title' && sortOrder === 'desc' },
  ];

  const handleSortOptionChange = (value: string) => {
    const [field, order] = value.split('_');
    const fieldMap: Record<string, string> = { created: 'created_at', title: 'title' };
    setSortBy(field === 'created' ? 'created_at' : fieldMap[field] || field);
    setSortOrder((order === 'asc' ? 'asc' : 'desc') as 'asc' | 'desc');
  };

  // ─── Filter bar actions ───────────────────────────────────────

  const filterActions = [
    ...(selectedIds.length > 0
      ? [{
          label: t('Delete ({{count}})', { count: selectedIds.length }),
          onClick: handleBulkDelete,
          icon: <PiTrashLight className="w-4 h-4" />,
        }]
      : []),
  ];

  // ─── Table ────────────────────────────────────────────────────

  const columns = ['Name', 'Status', 'Resources', 'Access', 'Owner', 'Created', 'Actions'];

  const tableData = items.map((item: any) => ({
    id: item.uuid,
    uuid: item.uuid,
    _title: item.title || '',
    'Name': (
      <span className="text-gray-900 dark:text-gray-100 truncate">{item.title || t('Untitled')}</span>
    ),
    'Status': (
      <Badge color={statusColorMap[item.status] || 'gray'}>
        {statusLabelMap[item.status] || item.status}
      </Badge>
    ),
    'Resources': (
      <span className="text-sm text-gray-600 dark:text-gray-400">{item.resource_count || 0}</span>
    ),
    'Access': item.has_access && item.has_access.length > 0 ? (
      <div className="flex -space-x-1.5">
        {item.has_access.slice(0, 5).map((user: any, idx: number) => (
          <div key={idx} className={clsx(idx > 0 && 'ml-[-0.5rem]')} style={{ zIndex: 5 - idx }}>
            <CollaboratorBadge collaborator={user} />
          </div>
        ))}
        {item.has_access.length > 5 && (
          <span className="ml-1 text-xs text-gray-400">+{item.has_access.length - 5}</span>
        )}
      </div>
    ) : (
      <span className="text-xs text-gray-400">—</span>
    ),
    'Owner': (
      <span className="text-sm text-gray-600 dark:text-gray-400 truncate">{item.owner?.name || '-'}</span>
    ),
    'Created': (
      <span className="text-sm text-gray-500 dark:text-gray-400">{formatDateTime(item.created_at)}</span>
    ),
  }));

  const tableActions = [
    {
      label: t('Activity'),
      onClick: (row: any) => {
        setAuditProjectUuid(row.uuid);
        setAuditProjectTitle(row._title);
        setShowAuditPanel(true);
      },
      icon: <PiClockCounterClockwiseLight className="w-4 h-4" />,
    },
    {
      label: t('Delete'),
      onClick: (row: any) => handleDelete(row.uuid, row._title),
      icon: <PiTrashLight className="w-4 h-4" />,
      isDestructive: true,
    },
  ];

  const sortableColumns = ['Name', 'Status', 'Resources', 'Created'];

  // ─── Render ───────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full">
      <Header
        breadcrumbs={[{ name: t('Projects'), href: '/projects', current: true }]}
        actions={[{ label: t('New Project'), onClick: () => setIsCreating(true), icon: <PiPlusLight className="w-4 h-4" />, variant: 'primary' as const, tourId: 'projects-add-button' }]}
      />

      <div data-tour-id="projects-list">
        <FilterBarWithTabs
          filters={filters}
          sortOptions={sortOptions}
          selectedFilters={selectedFilters}
          onFilterChange={handleFilterChange}
          onSortChange={handleSortOptionChange}
          sortValue={`${sortBy === 'created_at' ? 'created' : sortBy}_${sortOrder}`}
          onSearchChange={debouncedSearch}
          onClearAllFilters={handleClearAllFilters}
          actions={filterActions}
          selectionCount={selectedIds.length > 0 ? selectedIds.length : undefined}
        />
      </div>

      <div className="flex-grow overflow-y-auto relative">
        <Table
          columns={columns}
          data={tableData}
          enableSelection
          selectedIds={selectedIds}
          onSelect={(id: string) => setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])}
          onSelectAll={() => setSelectedIds(selectedIds.length === items.length ? [] : items.map((item: any) => item.uuid))}
          onRowClick={(row: any) => history.push(`/projects/${row.uuid}`)}
          actions={tableActions}
          isLoadingMore={isLoading}
          sortableColumns={sortableColumns}
          sortBy={sortBy === 'title' ? 'Name' : sortBy === 'status' ? 'Status' : sortBy === 'resource_count' ? 'Resources' : 'Created'}
          sortOrder={sortOrder}
          onSort={handleSortChange}
          total_items={totalItems}
          total_showing_items={items.length}
          setIsShowing={true}
          showingLabel={t('projects')}
          selectedCount={selectedIds.length}
          resizableColumns
          defaultColumnWidths={{
            'Name': 280,
            'Status': 120,
            'Resources': 100,
            'Access': 180,
            'Owner': 180,
            'Created': 180,
          }}
          storageKeyPrefix="projects"
          max="max-h-[calc(100vh-120px)]"
          emptyState={{
            icon: <PiFolderOpenLight className="w-12 h-12 text-gray-300" />,
            headline: t('No projects yet'),
            subtext: t('Create your first project to start organizing your work.'),
            button: { label: t('New Project'), onClick: () => setIsCreating(true) },
          }}
        />
      </div>

      <FeatureTour
        steps={projectsListOrientationSteps}
        run={isProjectsListTourRunning}
        onCallback={handleProjectsListTourCallback}
        continuous
      />

      {/* Audit log slide-over panel */}
      {showAuditPanel && auditProjectUuid && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/20 dark:bg-black/40 transition-opacity duration-300"
            onClick={() => setShowAuditPanel(false)}
          />
          <div className="fixed inset-y-0 right-0 z-50 w-96 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-2xl flex flex-col animate-slide-in-right">
            <AuditLogPanel
              entityType="project"
              entityId={auditProjectUuid}
              entityName={auditProjectTitle}
              onClose={() => setShowAuditPanel(false)}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectsList;
